import type { BillingPlan } from "@common/utils/types";
import type { AddonProduct } from "@/features/admin/checkout/types";

/**
 * Format an amount in cents as a currency string.
 * Stripe amounts are stored in cents.
 */
export function formatCurrency(amountInCents: number, currency: string = "USD"): string {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: currency || "USD" }).format(
        amountInCents / 100,
    );
}

/**
 * Format an amount in cents as a whole-dollar currency string (no ".00").
 * E.g. 14900 → "$149", 15050 → "$150.50"
 */
export function formatCurrencyWhole(amountInCents: number, currency: string = "USD"): string {
    return formatCurrency(amountInCents, currency).replace(/\.00$/, "");
}

/**
 * Return a color hex for a usage progress bar based on percentage.
 * Teal by default, orange at 80%, red at 100%.
 */
export function getBarColor(percent: number): string {
    if (percent >= 100) return "#FE2962";
    if (percent >= 80) return "#FCB718";
    return "#1a7c7f";
}

/**
 * Format a date value to a readable string.
 * Handles epoch seconds (from Stripe Sync Engine, stored as string) and ISO date strings.
 */
export function formatDate(value: string | null): string {
    if (!value) return "";

    const asNum = Number(value);
    if (!isNaN(asNum) && asNum > 0) {
        // Epoch seconds (Stripe convention)
        return new Date(asNum * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    }

    // ISO or date string fallback
    const date = new Date(value);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/**
 * Return a display label for the billing interval.
 */
export function formatIntervalBadge(interval: BillingPlan["billing_interval"]): string {
    if (interval === "year") return "Annual";
    if (interval === "month") return "Monthly";
    return "";
}

/**
 * Format all-time happy visits as a human-readable "time saved" string.
 * Each happy visit saves ~9 minutes of record-writing time.
 */
export function formatTimeSaved(allTimeHappyVisits: number): string | null {
    if (allTimeHappyVisits <= 0) return null;
    const totalMinutes = allTimeHappyVisits * 9;
    const hours = Math.floor(totalMinutes / 60);
    if (hours < 1) return `${totalMinutes} minutes`;
    return `${hours.toLocaleString()} hour${hours === 1 ? "" : "s"}`;
}

/** Apply annual discount to a unit amount in cents. Returns original amount if not annual or no discount. */
export function applyAnnualDiscount(unitAmount: number, discountPct: number, interval: string): number {
    if (interval !== "year" || discountPct <= 0) return unitAmount;
    return Math.round(unitAmount * (1 - discountPct / 100));
}

/** Returns true if an addon is a visit pack (Happy Visits). */
export function isVisitsAddon(addon: AddonProduct): boolean {
    if (addon.visits_per_unit && addon.visits_per_unit > 0) return true;
    return addon.name.toLowerCase().includes("happy visits");
}

/** Returns true if an addon is a PIMS integration (not a visits pack). */
export function isPimsAddon(addon: AddonProduct): boolean {
    if (isVisitsAddon(addon)) return false;
    return addon.name.toLowerCase().includes("pims") || addon.name.toLowerCase().includes("integration");
}
